import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { api } from '../../lib/api'
import {
  ArrowLeft,
  Mail,
  MailOpen,
  CheckCheck,
  ChevronRight,
  Users,
  Clock,
  Paperclip,
  X,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'

interface Message {
  id: number
  subject: string
  workflow_state: string
  is_unread: boolean
  message_count: number
  last_message: string
  last_message_at: string
  context_name: string | null
  participants: string[]
  participant_count: number
}

interface MessageDetail {
  id: number
  subject: string
  workflow_state: string
  context_name: string | null
  participants: Array<{ id: number; name: string }>
  messages: Array<{
    id: number
    author_id: number
    author_name: string
    body: string
    created_at: string
    attachments: Array<{
      id: number
      display_name: string
      size: number
      url: string
    }>
  }>
  message_count: number
}

interface MessagesResponse {
  messages: Message[]
  unread_count: number
  total: number
  scope: string
}

export default function ParentMessages() {
  const [selectedConversation, setSelectedConversation] = useState<number | null>(null)
  const queryClient = useQueryClient()

  // Fetch conversations list
  const { data, isLoading } = useQuery<MessagesResponse>({
    queryKey: ['parent-messages'],
    queryFn: async () => {
      const response = await api.get('/api/parent/messages')
      return response.data
    },
  })

  // Fetch single conversation detail
  const { data: conversationDetail, isLoading: isLoadingDetail } = useQuery<MessageDetail>({
    queryKey: ['parent-message-detail', selectedConversation],
    queryFn: async () => {
      const response = await api.get(`/api/parent/messages/${selectedConversation}`)
      return response.data
    },
    enabled: !!selectedConversation,
  })

  // Mark single as read
  const markReadMutation = useMutation({
    mutationFn: async (conversationId: number) => {
      await api.patch(`/api/parent/messages/${conversationId}/read`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-messages'] })
    },
  })

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await api.post('/api/parent/messages/read-all')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-messages'] })
    },
  })

  const handleSelectConversation = (convId: number, isUnread: boolean) => {
    setSelectedConversation(convId)
    if (isUnread) {
      markReadMutation.mutate(convId)
    }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-surface-card rounded-lg"></div>
          ))}
        </div>
      </div>
    )
  }

  const messages = data?.messages || []
  const unreadCount = data?.unread_count || 0

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/parent" className="p-2 hover:bg-surface-hover rounded-lg">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-400" />
            Messages
          </h1>
          <p className="text-gray-400 text-sm">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="btn btn-secondary flex items-center gap-2"
          >
            <CheckCheck className="w-4 h-4" />
            Mark All Read
          </button>
        )}
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="card text-center py-12">
          <MailOpen className="w-12 h-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400">No messages</p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <button
              key={msg.id}
              onClick={() => handleSelectConversation(msg.id, msg.is_unread)}
              className={`w-full text-left p-4 rounded-lg transition-colors ${
                msg.is_unread
                  ? 'bg-blue-900/30 border border-blue-500/30 hover:bg-blue-900/40'
                  : 'bg-surface-card/50 hover:bg-surface-hover'
              } ${selectedConversation === msg.id ? 'ring-2 ring-quest-primary' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Unread indicator */}
                <div className="pt-1">
                  {msg.is_unread ? (
                    <Mail className="w-5 h-5 text-blue-400" />
                  ) : (
                    <MailOpen className="w-5 h-5 text-gray-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Subject and context */}
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`font-medium truncate ${msg.is_unread ? 'text-white' : 'text-gray-300'}`}>
                      {msg.subject || '(No subject)'}
                    </p>
                    {msg.message_count > 1 && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-gray-700 text-gray-400">
                        {msg.message_count}
                      </span>
                    )}
                  </div>

                  {/* Context (course name) */}
                  {msg.context_name && (
                    <p className="text-xs text-quest-primary mb-1">{msg.context_name}</p>
                  )}

                  {/* Preview */}
                  <p className="text-sm text-gray-400 truncate">{msg.last_message}</p>

                  {/* Footer */}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {msg.participants.slice(0, 2).join(', ')}
                      {msg.participant_count > 2 && ` +${msg.participant_count - 2}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(msg.last_message_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-base rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700/50 flex items-center gap-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-2 hover:bg-surface-hover rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold truncate">
                  {conversationDetail?.subject || 'Loading...'}
                </h2>
                {conversationDetail?.context_name && (
                  <p className="text-sm text-quest-primary">{conversationDetail.context_name}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {isLoadingDetail ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-24 bg-surface-card rounded-lg"></div>
                  <div className="h-24 bg-surface-card rounded-lg"></div>
                </div>
              ) : (
                conversationDetail?.messages.map((msg) => (
                  <div key={msg.id} className="bg-surface-card/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-quest-accent">{msg.author_name}</span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(msg.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <div
                      className="prose prose-invert prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body) }}
                    />
                    {msg.attachments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-700/50">
                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          Attachments
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {msg.attachments.map((att) => (
                            <a
                              key={att.id}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600 transition-colors"
                            >
                              {att.display_name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Participants */}
            {conversationDetail && conversationDetail.participants.length > 0 && (
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-xs text-gray-500 mb-2">Participants</p>
                <div className="flex flex-wrap gap-2">
                  {conversationDetail.participants.map((p) => (
                    <span
                      key={p.id}
                      className="px-2 py-1 bg-surface-card rounded text-xs text-gray-300"
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
